import React, { useState, useEffect } from 'react';
import {
  X,
  Settings,
  Users,
  Calendar,
  UserCog,
  Hospital,
  BarChart3,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  ChevronRight,
  Check,
  Edit2,
  Trash2,
  Search,
  Plus,
  Filter,
  TrendingUp,
  Activity,
  UserPlus,
  CalendarCheck,
  AlertCircle,
  Palette,
  Shield,
  MessageSquare
} from 'lucide-react';
import { apiFetch } from '../../utils/apiClient';

const AdminPanelEnhanced = ({ isOpen, onClose, onSettingsUpdate }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    totalPatients: 0,
    totalAppointments: 0,
    totalRevenue: 0,
    confirmedAppointments: 0,
    completedAppointments: 0,
    totalDoctors: 0,
    adminUsers: 0,
    activeSessions: 0,
    chatSessions: 0,
    appointmentsByStatus: {},
    appointmentsByDepartment: {},
    recentAppointments: [],
    recentPatients: [],
    recentUsers: []
  });

  // Users management
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersSearchInput, setUsersSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  // Appointments management
  const [appointments, setAppointments] = useState([]);
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const [appointmentsTotalPages, setAppointmentsTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [appointmentsSearch, setAppointmentsSearch] = useState('');
  const [appointmentsSearchInput, setAppointmentsSearchInput] = useState('');

  // Patients management
  const [patients, setPatients] = useState([]);
  const [patientsPage, setPatientsPage] = useState(1);
  const [patientsTotalPages, setPatientsTotalPages] = useState(1);
  const [patientsSearch, setPatientsSearch] = useState('');
  const [patientsSearchInput, setPatientsSearchInput] = useState('');

  // Edit modals state
  const [editingUser, setEditingUser] = useState(null);
  const [editingPatient, setEditingPatient] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);

  // Hospitals management
  const [hospitals, setHospitals] = useState([]);

  // Theme settings
  const [settings, setSettings] = useState({
    mainBackgroundColor: '#f9fafb',
    headerBackgroundStart: '#2563eb',
    headerBackgroundEnd: '#1d4ed8',
    chatBackgroundColor: '#ffffff',
    userMessageColor: '#2563eb',
    botMessageColor: '#f3f4f6',
    primaryButtonColor: '#2563eb',
    primaryButtonHoverColor: '#1d4ed8',
    textColor: '#111827',
    borderColor: '#e5e7eb'
  });

  const [savedSettings, setSavedSettings] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');

  const presetThemes = [
    {
      name: 'Default Blue',
      settings: {
        mainBackgroundColor: '#f9fafb',
        headerBackgroundStart: '#2563eb',
        headerBackgroundEnd: '#1d4ed8',
        chatBackgroundColor: '#ffffff',
        userMessageColor: '#2563eb',
        botMessageColor: '#f3f4f6',
        primaryButtonColor: '#2563eb',
        primaryButtonHoverColor: '#1d4ed8',
        textColor: '#111827',
        borderColor: '#e5e7eb'
      }
    },
    {
      name: 'Dark Mode',
      settings: {
        mainBackgroundColor: '#1f2937',
        headerBackgroundStart: '#312e81',
        headerBackgroundEnd: '#1e1b4b',
        chatBackgroundColor: '#374151',
        userMessageColor: '#6366f1',
        botMessageColor: '#4b5563',
        primaryButtonColor: '#6366f1',
        primaryButtonHoverColor: '#4f46e5',
        textColor: '#f9fafb',
        borderColor: '#4b5563'
      }
    },
    {
      name: 'Green Nature',
      settings: {
        mainBackgroundColor: '#f0fdf4',
        headerBackgroundStart: '#16a34a',
        headerBackgroundEnd: '#15803d',
        chatBackgroundColor: '#ffffff',
        userMessageColor: '#16a34a',
        botMessageColor: '#dcfce7',
        primaryButtonColor: '#16a34a',
        primaryButtonHoverColor: '#15803d',
        textColor: '#14532d',
        borderColor: '#bbf7d0'
      }
    }
  ];

  const handleUnauthorized = () => {
    setAuthError('Your session has expired. Please log in again.');
    setIsAuthenticated(false);
    setIsLoading(false);
  };

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      console.log(`🔄 [ADMIN] Admin panel opened - Loading ${activeTab} data...`);
      loadData();
    } else if (isOpen && !isAuthenticated) {
      console.log('🔒 [ADMIN] Admin panel opened - Waiting for authentication...');
    }
  }, [isOpen, isAuthenticated, activeTab]);

  // Auto-refresh users when search or page changes
  useEffect(() => {
    if (isOpen && isAuthenticated && activeTab === 'users') {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersPage, usersSearch]);

  // Auto-refresh appointments when filters change
  useEffect(() => {
    if (isOpen && isAuthenticated && activeTab === 'appointments') {
      fetchAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentsPage, statusFilter, appointmentsSearch]);

  // Auto-refresh patients when filters change
  useEffect(() => {
    if (isOpen && isAuthenticated && activeTab === 'patients') {
      fetchPatients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientsPage, patientsSearch]);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json'
  });

  const loadData = async () => {
    switch (activeTab) {
      case 'dashboard':
        await fetchDashboardStats();
        break;
      case 'users':
        await fetchUsers();
        break;
      case 'appointments':
        await fetchAppointments();
        break;
      case 'patients':
        await fetchPatients();
        break;
      case 'hospitals':
        await fetchHospitals();
        break;
      case 'theme':
        await fetchSettings();
        break;
      default:
        break;
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 [ADMIN] Fetching dashboard stats from database...');

      const response = await apiFetch('/backend/api/admin/dashboard/stats', {
        headers: getAuthHeaders()
      });

      if (response.status === 401) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        console.error(`❌ [ADMIN] Failed to load dashboard stats: status ${response.status}`);
        alert(`Error loading dashboard: ${response.status}`);
        return;
      }

      const data = await response.json();
      console.log('📊 [ADMIN] Dashboard stats response:', data);
      if (data.success) {
        setDashboardStats(data.stats);
        console.log('✅ [ADMIN] Dashboard stats loaded successfully:', data.stats);
      } else {
        console.error('❌ [ADMIN] Failed to load dashboard stats:', data.error);
        alert(`Error loading dashboard: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ [ADMIN] Error fetching dashboard stats:', error);
      alert(`Network error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 [ADMIN] Fetching ALL users from database...');

      const searchParam = usersSearch ? `&search=${encodeURIComponent(usersSearch)}` : '';
      const response = await apiFetch(
        `backend/api/admin/users?page=${usersPage}&limit=20${searchParam}`,
        { headers: getAuthHeaders() }
      );
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!response.ok) {
        console.error(`❌ [ADMIN] Failed to load users: status ${response.status}`);
        alert(`Error loading users: ${response.status}`);
        return;
      }
      const data = await response.json();
      console.log('👥 [ADMIN] Users response:', data);
      if (data.success) {
        setUsers(data.users);
        setUsersTotalPages(data.totalPages || 1);
        console.log(`✅ [ADMIN] Loaded ${data.users.length} users (Total: ${data.total})`);
      } else {
        console.error('❌ [ADMIN] Failed to load users:', data.error);
        alert(`Error loading users: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ [ADMIN] Error fetching users:', error);
      alert(`Network error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 [ADMIN] Fetching ALL appointments from database...');

      const statusParam = statusFilter ? `&status=${encodeURIComponent(statusFilter)}` : '';
      const searchParam = appointmentsSearch ? `&search=${encodeURIComponent(appointmentsSearch)}` : '';
      const response = await apiFetch(
        `backend/api/admin/appointments?page=${appointmentsPage}&limit=20${statusParam}${searchParam}`,
        { headers: getAuthHeaders() }
      );
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!response.ok) {
        console.error(`❌ [ADMIN] Failed to load appointments: status ${response.status}`);
        alert(`Error loading appointments: ${response.status}`);
        return;
      }
      const data = await response.json();
      console.log('📅 [ADMIN] Appointments response:', data);
      if (data.success) {
        setAppointments(data.appointments);
        setAppointmentsTotalPages(data.totalPages || 1);
        console.log(`✅ [ADMIN] Loaded ${data.appointments.length} appointments (Total: ${data.total})`);
        // Log first appointment structure for debugging
        if (data.appointments.length > 0) {
          console.log('📋 [ADMIN] First appointment FULL OBJECT:', data.appointments[0]);
          console.log('📋 [ADMIN] First appointment ID specifically:', data.appointments[0].id);
          console.log('📋 [ADMIN] First appointment keys:', Object.keys(data.appointments[0]));
        }
      } else {
        console.error('❌ [ADMIN] Failed to load appointments:', data.error);
        alert(`Error loading appointments: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ [ADMIN] Error fetching appointments:', error);
      alert(`Network error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 [ADMIN] Fetching ALL patients from database...');

      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to access the admin panel');
        onClose();
        return;
      }

      const searchParam = patientsSearch ? `&search=${encodeURIComponent(patientsSearch)}` : '';
      const response = await apiFetch(
        `backend/api/admin/patients?page=${patientsPage}&limit=20${searchParam}`,
        { headers }
      );
      const data = await response.json();
      console.log('🏥 [ADMIN] Patients response:', data);
      if (data.success) {
        setPatients(data.patients);
        setPatientsTotalPages(data.totalPages || 1);
        console.log(`✅ [ADMIN] Loaded ${data.patients.length} patients (Total: ${data.total})`);
        console.log('📋 [ADMIN] Patient UHIDs:', data.patients.map(p => p.UHID).join(', '));
        // Log first patient structure for debugging
        if (data.patients.length > 0) {
          console.log('📋 [ADMIN] First patient FULL OBJECT:', data.patients[0]);
          console.log('📋 [ADMIN] First patient ID specifically:', data.patients[0].id);
          console.log('📋 [ADMIN] First patient keys:', Object.keys(data.patients[0]));
        }
      } else{
        console.error('❌ [ADMIN] Failed to load patients:', data.error);
        alert(`Error loading patients: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ [ADMIN] Error fetching patients:', error);
      alert(`Network error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHospitals = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 [ADMIN] Fetching ALL hospitals from database...');

      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to access the admin panel');
        onClose();
        return;
      }

      const response = await apiFetch('/backend/api/admin/hospitals', {
        headers
      });
      const data = await response.json();
      console.log('🏨 [ADMIN] Hospitals response:', data);
      if (data.success) {
        setHospitals(data.hospitals);
        console.log(`✅ [ADMIN] Loaded ${data.hospitals.length} hospitals`);
      } else {
        console.error('❌ [ADMIN] Failed to load hospitals:', data.error);
        alert(`Error loading hospitals: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ [ADMIN] Error fetching hospitals:', error);
      alert(`Network error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setIsLoading(true);

      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to access the admin panel');
        onClose();
        return;
      }

      const response = await apiFetch('/backend/api/admin/settings', {
        headers
      });
      const data = await response.json();
      if (data.success && data.settings) {
        setSettings(data.settings);
        setSavedSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    if (password === 'Admin@123') {
      console.log('✅ [ADMIN] Admin password authenticated successfully!');
      setIsAuthenticated(true);
      setAuthError('');
      setPassword('');
    } else {
      console.log('❌ [ADMIN] Incorrect admin password entered');
      setAuthError('Incorrect password');
    }
  };

  const handleDeleteUser = async (userId) => {
    const normalizedUserId = (userId ?? '').toString().trim();
    if (!normalizedUserId || normalizedUserId === 'undefined' || normalizedUserId === 'null') {
      alert('Error: User ID not found. Please refresh the page and try again.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to perform this action');
        return;
      }

      const response = await apiFetch(`/backend/api/admin/users/${normalizedUserId}`, {
        method: 'DELETE',
        headers
      });
      const data = await response.json();
      if (data.success) {
        alert('User deleted successfully');
        fetchUsers();
      } else {
        alert(data.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user: ' + error.message);
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      const userId = (user?.user_id ?? user?.id ?? '').toString().trim();
      if (!userId) {
        alert('Error: User ID not found. Please refresh the page and try again.');
        return;
      }

      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to perform this action');
        return;
      }

      const response = await apiFetch(`/backend/api/admin/users/${userId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          is_admin: !user.is_admin
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('User updated successfully');
        fetchUsers();
      } else {
        alert(data.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user: ' + error.message);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    console.log('🗑️ [DELETE] handleDeleteAppointment called with ID:', appointmentId);

    const normalizedAppointmentId = (appointmentId ?? '').toString().trim();

    // Validate appointment ID
    if (!normalizedAppointmentId || normalizedAppointmentId === 'undefined' || normalizedAppointmentId === 'null') {
      console.error('❌ [DELETE] Invalid appointment ID:', appointmentId);
      alert('Error: Appointment ID not found. Please refresh the page and try again.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this appointment?')) return;

    try {
      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to perform this action');
        return;
      }

      console.log('🔄 [DELETE] Sending DELETE request for appointment:', normalizedAppointmentId);
      const response = await apiFetch(
        `/backend/api/admin/appointments/${normalizedAppointmentId}`,
        {
          method: 'DELETE',
          headers
        }
      );
      const data = await response.json();
      if (data.success) {
        alert('Appointment deleted successfully');
        fetchAppointments();
      } else {
        alert(data.error || 'Failed to delete appointment');
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Error deleting appointment: ' + error.message);
    }
  };

  const handleDeletePatient = async (patientId) => {
    console.log('🗑️ [DELETE] handleDeletePatient called with ID:', patientId);

    const normalizedPatientId = (patientId ?? '').toString().trim();

    // Validate patient ID
    if (!normalizedPatientId || normalizedPatientId === 'undefined' || normalizedPatientId === 'null') {
      console.error('❌ [DELETE] Invalid patient ID:', patientId);
      alert('Error: Patient ID not found. Please refresh the page and try again.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this patient?')) return;

    try {
      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to perform this action');
        return;
      }

      console.log('🔄 [DELETE] Sending DELETE request for patient:', normalizedPatientId);
      const response = await apiFetch(
        `/backend/api/admin/patients/${normalizedPatientId}`,
        {
          method: 'DELETE',
          headers
        }
      );
      const data = await response.json();
      if (data.success) {
        alert('Patient deleted successfully');
        fetchPatients();
      } else {
        alert(data.error || 'Failed to delete patient');
      }
    } catch (error) {
      console.error('Error deleting patient:', error);
      alert('Error deleting patient: ' + error.message);
    }
  };

  const handleUsersSearchSubmit = () => {
    setUsersPage(1);
    setUsersSearch(usersSearchInput.trim());
  };

  const handleUsersSearchClear = () => {
    setUsersSearchInput('');
    setUsersPage(1);
    setUsersSearch('');
  };

  const handleAppointmentsSearchSubmit = () => {
    setAppointmentsPage(1);
    setAppointmentsSearch(appointmentsSearchInput.trim());
  };

  const handleAppointmentsSearchClear = () => {
    setAppointmentsSearchInput('');
    setAppointmentsPage(1);
    setAppointmentsSearch('');
  };

  const handlePatientsSearchSubmit = () => {
    setPatientsPage(1);
    setPatientsSearch(patientsSearchInput.trim());
  };

  const handlePatientsSearchClear = () => {
    setPatientsSearchInput('');
    setPatientsPage(1);
    setPatientsSearch('');
  };

  // Edit User Handler
  const handleEditUser = (user) => {
    setEditingUser({
      user_id: user.user_id || user.id,
      fullName: user.fullName || user.name || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || user.phone || '',
      is_admin: user.is_admin || false,
      status: user.status || 'Active'
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to perform this action');
        return;
      }

      const response = await apiFetch(`/backend/api/admin/users/${editingUser.user_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          fullName: editingUser.fullName,
          email: editingUser.email,
          phoneNumber: editingUser.phoneNumber,
          is_admin: editingUser.is_admin,
          status: editingUser.status
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('User updated successfully');
        setEditingUser(null);
        fetchUsers();
      } else {
        alert(data.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user: ' + error.message);
    }
  };

  // Edit Patient Handler
  const handleEditPatient = (patient) => {
    setEditingPatient({
      uhid: patient.uhid,
      fullName: patient.fullName || patient.name || '',
      age: patient.age || '',
      gender: patient.gender || '',
      phoneNumber: patient.phoneNumber || patient.contactNumber || '',
      city: patient.city || '',
      pincode: patient.pincode || '',
      fullAddress: patient.fullAddress || patient.address || ''
    });
  };

  const handleSavePatient = async () => {
    if (!editingPatient) return;

    try {
      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to perform this action');
        return;
      }

      const response = await apiFetch(`/backend/api/admin/patients/${editingPatient.uhid}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          fullName: editingPatient.fullName,
          age: parseInt(editingPatient.age) || editingPatient.age,
          gender: editingPatient.gender,
          phoneNumber: editingPatient.phoneNumber,
          city: editingPatient.city,
          pincode: editingPatient.pincode,
          fullAddress: editingPatient.fullAddress
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('Patient updated successfully');
        setEditingPatient(null);
        fetchPatients();
      } else {
        alert(data.error || 'Failed to update patient');
      }
    } catch (error) {
      console.error('Error updating patient:', error);
      alert('Error updating patient: ' + error.message);
    }
  };

  // Edit Appointment Handler
  const handleEditAppointment = (appointment) => {
    setEditingAppointment({
      appointment_id: appointment.appointment_id || appointment.id,
      uhid: appointment.uhid || '',
      patientName: appointment.patientName || appointment.patient || '',
      doctor: appointment.doctor || '',
      hospital: appointment.hospital || appointment.department || '',
      date: appointment.date || appointment.appointmentDate || '',
      slot: appointment.slot || appointment.time || '',
      status: appointment.status || 'pending'
    });
  };

  const handleSaveAppointment = async () => {
    if (!editingAppointment) return;

    try {
      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to perform this action');
        return;
      }

      const response = await apiFetch(`/backend/api/admin/appointments/${editingAppointment.appointment_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          date: editingAppointment.date,
          slot: editingAppointment.slot,
          status: editingAppointment.status,
          doctor: editingAppointment.doctor,
          hospital: editingAppointment.hospital
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('Appointment updated successfully');
        setEditingAppointment(null);
        fetchAppointments();
      } else {
        alert(data.error || 'Failed to update appointment');
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      alert('Error updating appointment: ' + error.message);
    }
  };

  const handleColorChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));

    if (onSettingsUpdate) {
      onSettingsUpdate({ ...settings, [field]: value });
    }
  };

  const applyTheme = (theme) => {
    setSettings(theme.settings);
    if (onSettingsUpdate) {
      onSettingsUpdate(theme.settings);
    }
  };

  const handleSaveTheme = async () => {
    try {
      setIsLoading(true);
      setSaveStatus('saving');

      const headers = getAuthHeaders();
      if (!headers) {
        alert('Please login first to save settings');
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 3000);
        return;
      }

      const response = await apiFetch('/backend/api/admin/settings', {
        method: 'POST',
        headers,
        body: JSON.stringify({ settings })
      });

      const data = await response.json();

      if (data.success) {
        setSavedSettings(settings);
        setSaveStatus('saved');

        if (onSettingsUpdate) {
          onSettingsUpdate(settings);
        }

        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setActiveTab('dashboard');
  };

  if (!isOpen) return null;

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Admin Panel</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          <p className="text-gray-600 mb-6 text-center">
            Please enter the admin password to access the management panel.
          </p>

          <div className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setAuthError('');
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {authError && <p className="text-red-500 text-sm">{authError}</p>}

            <button
              onClick={handleLogin}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <Unlock className="w-5 h-5" />
              <span>Login</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Admin Panel
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Settings className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Admin Management Panel</h2>
                <p className="text-indigo-100">Complete system administration</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleLogout}
                className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Lock className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <div className="w-64 bg-gray-50 border-r border-gray-200 p-4 overflow-y-auto">
            <nav className="space-y-2">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'dashboard'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span className="font-medium">Dashboard</span>
              </button>

              <button
                onClick={() => setActiveTab('users')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'users'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Users</span>
              </button>

              <button
                onClick={() => setActiveTab('appointments')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'appointments'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Calendar className="w-5 h-5" />
                <span className="font-medium">Appointments</span>
              </button>

              <button
                onClick={() => setActiveTab('patients')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'patients'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <UserCog className="w-5 h-5" />
                <span className="font-medium">Patients</span>
              </button>

              <button
                onClick={() => setActiveTab('hospitals')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'hospitals'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Hospital className="w-5 h-5" />
                <span className="font-medium">Hospitals</span>
              </button>

              <button
                onClick={() => setActiveTab('theme')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === 'theme'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Palette className="w-5 h-5" />
                <span className="font-medium">Theme Settings</span>
              </button>
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading && (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
              </div>
            )}

            {/* Dashboard Tab */}
            {!isLoading && activeTab === 'dashboard' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-800">Dashboard Overview</h3>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Total Users</p>
                        <p className="text-3xl font-bold mt-2">{dashboardStats.totalUsers}</p>
                      </div>
                      <Users className="w-12 h-12 text-blue-200" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Total Patients</p>
                        <p className="text-3xl font-bold mt-2">{dashboardStats.totalPatients}</p>
                      </div>
                      <UserCog className="w-12 h-12 text-green-200" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Total Appointments</p>
                        <p className="text-3xl font-bold mt-2">{dashboardStats.totalAppointments}</p>
                      </div>
                      <Calendar className="w-12 h-12 text-purple-200" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">Active Users</p>
                        <p className="text-3xl font-bold mt-2">{dashboardStats.activeUsers}</p>
                      </div>
                      <Activity className="w-12 h-12 text-orange-200" />
                    </div>
                  </div>
                </div>

                {/* Additional Stats Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <CalendarCheck className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Today's Appointments</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboardStats.todayAppointments}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Pending Appointments</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboardStats.pendingAppointments}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Confirmed</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboardStats.confirmedAppointments || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Check className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Completed</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboardStats.completedAppointments || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Stats Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <UserPlus className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Total Doctors</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboardStats.totalDoctors}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <Shield className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Admin Users</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboardStats.adminUsers || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        <Activity className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Active Sessions</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboardStats.activeSessions || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                        <MessageSquare className="w-6 h-6 text-pink-600" />
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Chat Sessions</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboardStats.chatSessions || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Appointments by Status Chart */}
                {dashboardStats.appointmentsByStatus && Object.keys(dashboardStats.appointmentsByStatus).length > 0 && (
                  <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Appointments by Status</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <p className="text-3xl font-bold text-yellow-600">
                          {dashboardStats.appointmentsByStatus.pending || 0}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Pending</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-3xl font-bold text-green-600">
                          {dashboardStats.appointmentsByStatus.confirmed || 0}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Confirmed</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-3xl font-bold text-blue-600">
                          {dashboardStats.appointmentsByStatus.completed || 0}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Completed</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <p className="text-3xl font-bold text-red-600">
                          {dashboardStats.appointmentsByStatus.cancelled || 0}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Cancelled</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Appointments by Department */}
                {dashboardStats.appointmentsByDepartment && Object.keys(dashboardStats.appointmentsByDepartment).length > 0 && (
                  <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Appointments by Department</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(dashboardStats.appointmentsByDepartment || {}).map(([dept, count]) => (
                        <div key={dept} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700">{dept}</span>
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Appointments */}
                {dashboardStats.recentAppointments && dashboardStats.recentAppointments.length > 0 && (
                  <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Recent Appointments</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">UHID</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Patient</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Doctor</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Department</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dashboardStats.recentAppointments || []).map((appt) => (
                            <tr key={appt.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4 text-sm text-gray-800">{appt.uhid || 'N/A'}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{appt.patientName || 'N/A'}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{appt.doctor || 'N/A'}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{appt.hospital || 'N/A'}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">
                                {appt.date ? appt.date : (appt.appointmentDateTime ? new Date(appt.appointmentDateTime).toLocaleDateString() : 'N/A')}
                              </td>
                              <td className="py-3 px-4">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    appt.status === 'confirmed'
                                      ? 'bg-green-100 text-green-700'
                                      : appt.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : appt.status === 'completed'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {appt.status || 'N/A'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recent Patients */}
                {dashboardStats.recentPatients && dashboardStats.recentPatients.length > 0 && (
                  <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Recent Patients</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">UHID</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Name</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Age</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Gender</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Mobile</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Registered By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(dashboardStats.recentPatients || []).map((patient) => (
                            <tr key={patient.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4 text-sm font-medium text-gray-800">{patient.uhid}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{patient.fullName}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{patient.age}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{patient.gender}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{patient.phoneNumber}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">
                                {patient.registrationByEmail || patient.registrationByNumber}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recent Users */}
                <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                  <h4 className="text-lg font-bold text-gray-800 mb-4">Recent Users</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Name</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Email</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Phone</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Role</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Registered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(dashboardStats.recentUsers || []).map((user) => (
                          <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm text-gray-800">{user.fullName}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{user.email}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{user.phoneNumber}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.is_admin
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {user.is_admin ? 'Admin' : user.role}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600">
                              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {!isLoading && activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-gray-800">User Management</h3>
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={usersSearchInput}
                        onChange={(e) => setUsersSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleUsersSearchSubmit();
                          }
                        }}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <button
                      onClick={handleUsersSearchSubmit}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Search className="w-5 h-5" />
                      <span>Search</span>
                    </button>
                    {usersSearch || usersSearchInput ? (
                      <button
                        onClick={handleUsersSearchClear}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors text-sm"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Name</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Email</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Phone</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Role</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Status</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Last Login</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => {
                          const userId = user.user_id || user.id || user.email || `user-${index}`;
                          const lastLogin = user.lastLogin || user.last_login;
                          return (
                            <tr key={userId} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="py-4 px-6 text-sm text-gray-800">{user.fullName || user.name || 'N/A'}</td>
                              <td className="py-4 px-6 text-sm text-gray-600">{user.email || 'N/A'}</td>
                              <td className="py-4 px-6 text-sm text-gray-600">{user.phoneNumber || user.phone || 'N/A'}</td>
                              <td className="py-4 px-6">
                                <span
                                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    user.is_admin ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                                  }`}
                                >
                                  {user.is_admin ? 'Admin' : (user.role || 'User')}
                                </span>
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-600">
                                {typeof user.status === 'string'
                                  ? user.status
                                  : user.status === false
                                    ? 'Inactive'
                                    : 'Active'}
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-600">
                                {lastLogin ? new Date(lastLogin).toLocaleString() : 'Never'}
                              </td>
                              <td className="py-4 px-6 text-right space-x-2">
                                <button
                                  onClick={() => handleEditUser(user)}
                                  className="px-3 py-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors inline-flex items-center space-x-1"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  <span className="text-xs font-medium">Edit</span>
                                </button>
                                <button
                                  onClick={() => handleToggleAdmin(user)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    user.is_admin
                                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                >
                                  {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user.user_id || user.id)}
                                  className="px-3 py-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors inline-flex items-center space-x-1"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span className="text-xs font-medium">Delete</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {users.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-6 px-6 text-center text-sm text-gray-500">
                              No users found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Page {usersPage} of {usersTotalPages}
                    </p>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setUsersPage(Math.max(1, usersPage - 1))}
                        disabled={usersPage === 1}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setUsersPage(Math.min(usersTotalPages, usersPage + 1))}
                        disabled={usersPage === usersTotalPages}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Appointments Tab */}
            {!isLoading && activeTab === 'appointments' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-gray-800">Appointment Management</h3>
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search appointments..."
                        value={appointmentsSearchInput}
                        onChange={(e) => setAppointmentsSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAppointmentsSearchSubmit();
                          }
                        }}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-300">
                      <Filter className="w-4 h-4 text-gray-600" />
                      <select
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setAppointmentsPage(1); // Reset to page 1 when filter changes
                        }}
                        className="bg-transparent border-none focus:outline-none focus:ring-0 text-sm cursor-pointer"
                      >
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <button
                      onClick={handleAppointmentsSearchSubmit}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Search className="w-4 h-4" />
                      <span className="text-sm">Search</span>
                    </button>
                    {appointmentsSearch || appointmentsSearchInput ? (
                      <button
                        onClick={handleAppointmentsSearchClear}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors text-sm"
                      >
                        Clear
                      </button>
                    ) : null}
                    <button
                      onClick={fetchAppointments}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                      title="Refresh appointments"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span className="text-sm">Refresh</span>
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Appointment ID</th>
                          <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Patient Name</th>
                          <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">UHID</th>
                          <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Doctor</th>
                          <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Hospital</th>
                          <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Date &amp; Time</th>
                          <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Status</th>
                          <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appointments.map((appt, index) => {
                          const appointmentId = appt.appointment_id || appt.id || appt._id || `appointment-${index}`;
                          const displayStatus = (appt.status || 'unknown').toLowerCase();
                          const statusClass =
                            displayStatus === 'confirmed'
                              ? 'bg-green-100 text-green-700'
                              : displayStatus === 'completed'
                              ? 'bg-blue-100 text-blue-700'
                              : displayStatus === 'cancelled'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700';

                          const appointmentDate =
                            appt.date ||
                            appt.appointmentDate ||
                            (appt.appointmentDateTime ? new Date(appt.appointmentDateTime).toLocaleString() : null);

                          return (
                            <tr key={appointmentId} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="py-4 px-4 text-sm text-gray-600">{appointmentId}</td>
                              <td className="py-4 px-4 text-sm text-gray-800 font-medium">
                                {appt.patientName || appt.patient_name || appt.patient || 'N/A'}
                              </td>
                              <td className="py-4 px-4 text-sm text-gray-600">{appt.uhid || 'N/A'}</td>
                              <td className="py-4 px-4 text-sm text-gray-600">{appt.doctor || 'N/A'}</td>
                              <td className="py-4 px-4 text-sm text-gray-600">{appt.hospital || appt.department || 'N/A'}</td>
                              <td className="py-4 px-4 text-sm text-gray-600">{appointmentDate || 'N/A'}</td>
                              <td className="py-4 px-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusClass}`}>
                                  {appt.status || 'Unknown'}
                                </span>
                              </td>
                              <td className="py-4 px-4 text-right space-x-2">
                                <button
                                  onClick={() => handleEditAppointment(appt)}
                                  className="px-3 py-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors inline-flex items-center space-x-1"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  <span className="text-xs font-medium">Edit</span>
                                </button>
                                <button
                                  onClick={() => handleDeleteAppointment(appt.appointment_id || appt.id)}
                                  className="px-3 py-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors inline-flex items-center space-x-1"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span className="text-xs font-medium">Delete</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {appointments.length === 0 && (
                          <tr>
                            <td colSpan={8} className="py-6 px-6 text-center text-sm text-gray-500">
                              No appointments found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Page {appointmentsPage} of {appointmentsTotalPages}
                    </p>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setAppointmentsPage(Math.max(1, appointmentsPage - 1))}
                        disabled={appointmentsPage === 1}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() =>
                          setAppointmentsPage(Math.min(appointmentsTotalPages, appointmentsPage + 1))
                        }
                        disabled={appointmentsPage === appointmentsTotalPages}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Patients Tab */}
            {!isLoading && activeTab === 'patients' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-gray-800">Patient Management</h3>
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search patients..."
                        value={patientsSearchInput}
                        onChange={(e) => setPatientsSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handlePatientsSearchSubmit();
                          }
                        }}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <button
                      onClick={handlePatientsSearchSubmit}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                    >
                      <Search className="w-5 h-5" />
                    </button>
                    {patientsSearch || patientsSearchInput ? (
                      <button
                        onClick={handlePatientsSearchClear}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors text-sm"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">UHID</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Name</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Gender</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Age</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Phone</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Registered On</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {patients.map((patient, index) => {
                          const patientId = patient.uhid || patient.id || patient.phoneNumber || `patient-${index}`;
                          const registeredOn = patient.registrationDate || patient.createdAt;
                          return (
                            <tr key={patientId} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="py-4 px-6 text-sm text-gray-600">{patient.uhid || 'N/A'}</td>
                              <td className="py-4 px-6 text-sm text-gray-800">{patient.fullName || patient.name || 'N/A'}</td>
                              <td className="py-4 px-6 text-sm text-gray-600">{patient.gender || 'N/A'}</td>
                              <td className="py-4 px-6 text-sm text-gray-600">{patient.age || 'N/A'}</td>
                              <td className="py-4 px-6 text-sm text-gray-600">{patient.phoneNumber || patient.contactNumber || 'N/A'}</td>
                              <td className="py-4 px-6 text-sm text-gray-600">
                                {registeredOn ? new Date(registeredOn).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="py-4 px-6 text-right space-x-2">
                                <button
                                  onClick={() => handleEditPatient(patient)}
                                  className="px-3 py-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors inline-flex items-center space-x-1"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  <span className="text-xs font-medium">Edit</span>
                                </button>
                                <button
                                  onClick={() => handleDeletePatient(patient.uhid || patient.id)}
                                  className="px-3 py-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors inline-flex items-center space-x-1"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span className="text-xs font-medium">Delete</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {patients.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-6 px-6 text-center text-sm text-gray-500">
                              No patients found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Page {patientsPage} of {patientsTotalPages}
                    </p>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPatientsPage(Math.max(1, patientsPage - 1))}
                        disabled={patientsPage === 1}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPatientsPage(Math.min(patientsTotalPages, patientsPage + 1))}
                        disabled={patientsPage === patientsTotalPages}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Hospitals Tab */}
            {!isLoading && activeTab === 'hospitals' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-800">Hospital Management</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {hospitals.map((hospital) => (
                    <div key={hospital.id} className="bg-white rounded-xl border-2 border-gray-200 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                            <Hospital className="w-6 h-6 text-indigo-600" />
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-gray-800">{hospital.name}</h4>
                            <p className="text-sm text-gray-600">{hospital.location}</p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          {hospital.status}
                        </span>
                      </div>

                      <div className="space-y-2 text-sm text-gray-600">
                        <p>
                          <span className="font-medium">Phone:</span> {hospital.phone}
                        </p>
                        <p>
                          <span className="font-medium">Email:</span> {hospital.email}
                        </p>
                        <p>
                          <span className="font-medium">Departments:</span> {hospital.departments}
                        </p>
                        <p>
                          <span className="font-medium">Doctors:</span> {hospital.doctors}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Theme Settings Tab */}
            {!isLoading && activeTab === 'theme' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-800">Theme Settings</h3>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Preset Themes */}
                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-gray-700">Preset Themes</h4>
                    {presetThemes.map((theme, index) => (
                      <button
                        key={index}
                        onClick={() => applyTheme(theme)}
                        className="w-full p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-indigo-500 transition-colors text-left group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">{theme.name}</span>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-500" />
                        </div>
                        <div className="mt-2 flex space-x-1">
                          {Object.values(theme.settings)
                            .slice(0, 5)
                            .map((color, i) => (
                              <div
                                key={i}
                                className="w-6 h-6 rounded border border-gray-300"
                                style={{ backgroundColor: color }}
                              />
                            ))}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Color Customization */}
                  <div className="lg:col-span-2">
                    <h4 className="text-lg font-semibold text-gray-700 mb-4">Color Customization</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.keys(settings).map((field) => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {field
                              .replace(/([A-Z])/g, ' $1')
                              .replace(/^./, (str) => str.toUpperCase())}
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="color"
                              value={settings[field]}
                              onChange={(e) => handleColorChange(field, e.target.value)}
                              className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={settings[field]}
                              onChange={(e) => handleColorChange(field, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 flex items-center justify-end space-x-3">
                      {saveStatus === 'saved' && (
                        <div className="flex items-center space-x-2 text-green-600">
                          <Check className="w-5 h-5" />
                          <span className="text-sm font-medium">Saved successfully!</span>
                        </div>
                      )}
                      {saveStatus === 'error' && (
                        <span className="text-red-600 text-sm font-medium">Error saving settings</span>
                      )}

                      <button
                        onClick={handleSaveTheme}
                        disabled={isLoading || saveStatus === 'saving'}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                          isLoading || saveStatus === 'saving'
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                        }`}
                      >
                        {saveStatus === 'saving' ? (
                          <RefreshCw className="w-5 h-5 animate-spin" />
                        ) : (
                          <Save className="w-5 h-5" />
                        )}
                        <span>{saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">Edit User</h3>
                <button
                  onClick={() => setEditingUser(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={editingUser.fullName}
                  onChange={(e) => setEditingUser({ ...editingUser, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={editingUser.phoneNumber}
                  onChange={(e) => setEditingUser({ ...editingUser, phoneNumber: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editingUser.status}
                  onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isAdmin"
                  checked={editingUser.is_admin}
                  onChange={(e) => setEditingUser({ ...editingUser, is_admin: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="isAdmin" className="text-sm font-medium text-gray-700">Admin User</label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Patient Modal */}
      {editingPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">Edit Patient</h3>
                <button
                  onClick={() => setEditingPatient(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">UHID: {editingPatient.uhid}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={editingPatient.fullName}
                  onChange={(e) => setEditingPatient({ ...editingPatient, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <input
                    type="number"
                    value={editingPatient.age}
                    onChange={(e) => setEditingPatient({ ...editingPatient, age: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select
                    value={editingPatient.gender}
                    onChange={(e) => setEditingPatient({ ...editingPatient, gender: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={editingPatient.phoneNumber}
                  onChange={(e) => setEditingPatient({ ...editingPatient, phoneNumber: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={editingPatient.city}
                    onChange={(e) => setEditingPatient({ ...editingPatient, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    value={editingPatient.pincode}
                    onChange={(e) => setEditingPatient({ ...editingPatient, pincode: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
                <textarea
                  value={editingPatient.fullAddress}
                  onChange={(e) => setEditingPatient({ ...editingPatient, fullAddress: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setEditingPatient(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePatient}
                className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Appointment Modal */}
      {editingAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">Edit Appointment</h3>
                <button
                  onClick={() => setEditingAppointment(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">ID: {editingAppointment.appointment_id}</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UHID</label>
                  <input
                    type="text"
                    value={editingAppointment.uhid}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name</label>
                  <input
                    type="text"
                    value={editingAppointment.patientName}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Doctor</label>
                <input
                  type="text"
                  value={editingAppointment.doctor}
                  onChange={(e) => setEditingAppointment({ ...editingAppointment, doctor: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hospital</label>
                <input
                  type="text"
                  value={editingAppointment.hospital}
                  onChange={(e) => setEditingAppointment({ ...editingAppointment, hospital: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={editingAppointment.date}
                    onChange={(e) => setEditingAppointment({ ...editingAppointment, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time Slot</label>
                  <input
                    type="text"
                    value={editingAppointment.slot}
                    onChange={(e) => setEditingAppointment({ ...editingAppointment, slot: e.target.value })}
                    placeholder="e.g., 10:00 AM - 10:30 AM"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editingAppointment.status}
                  onChange={(e) => setEditingAppointment({ ...editingAppointment, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setEditingAppointment(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAppointment}
                className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanelEnhanced;
